import React from 'react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../../services/firebase';
import { fetchMedications } from '../../../services/medicationService';
import { saveMedicationRecord } from '../../Dashboard/shared/medicationPersistence';
import { createMedicationDetail } from '../../Dashboard/shared/childMedicationHelpers';
import {
  buildConfirmedMedicationAdministrations,
  buildMedicationAdministrationOptions,
  getMedicationAdministrationReviewCopy,
  getInitialMedicationAdministrationSelection,
} from '../../../utils/medicationAdministrationReview';

const getReview = (entry = {}) => entry?.metadata?.medicationAdministrationReview || null;

export const useMedicationAdministrationReview = ({ entryId = '', entryData = {} } = {}) => {
  const review = getReview(entryData);
  const storedAdministrations = Array.isArray(entryData?.medicationAdministrations)
    ? entryData.medicationAdministrations
    : [];
  const storedAdministrationsKey = JSON.stringify(storedAdministrations);
  const [status, setStatus] = React.useState(review?.status || '');
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [administrationStatus, setAdministrationStatus] = React.useState(
    storedAdministrations[0]?.status || review?.administrationStatus || 'uncertain'
  );
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [addOpen, setAddOpen] = React.useState(false);
  const [addDraft, setAddDraft] = React.useState(null);
  const [confirmedAdministrations, setConfirmedAdministrations] = React.useState(
    storedAdministrations
  );
  const operationInFlightRef = React.useRef(false);
  const childId = entryData?.childId || '';
  const entryCollection = entryData?.collection || 'dailyLogs';
  const isPending = status === 'pending' && Boolean(review);
  const copy = getMedicationAdministrationReviewCopy(review || {});

  React.useEffect(() => {
    setStatus(review?.status || '');
  }, [review?.status]);

  React.useEffect(() => {
    setConfirmedAdministrations(storedAdministrations);
    // The serialized key prevents loops when a parent reconstructs an equal array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedAdministrationsKey]);

  const loadOptions = React.useCallback(async () => {
    const medications = await fetchMedications(childId, true);
    const nextOptions = buildMedicationAdministrationOptions(medications, review || {});
    setOptions(nextOptions);
    return nextOptions;
  }, [childId, review]);

  const openReview = React.useCallback(async () => {
    if ((!isPending && status !== 'confirmed') || loading || operationInFlightRef.current) return;
    if (!childId) {
      setError('Could not identify whose medication list to load.');
      return;
    }
    operationInFlightRef.current = true;
    setLoading(true);
    setError('');
    try {
      const nextOptions = await loadOptions();
      const confirmedIds = confirmedAdministrations
        .map((administration) => administration?.medicationId)
        .filter((medicationId) => nextOptions.some((option) => option.id === medicationId));
      setSelectedIds(
        confirmedIds.length
          ? confirmedIds
          : getInitialMedicationAdministrationSelection(nextOptions, review || {})
      );
      setAdministrationStatus(
        confirmedAdministrations[0]?.status || review?.administrationStatus || 'uncertain'
      );
      setSearch('');
      setOpen(true);
    } catch (loadError) {
      setError(loadError?.message || 'Could not load the medication list.');
    } finally {
      operationInFlightRef.current = false;
      setLoading(false);
    }
  }, [childId, confirmedAdministrations, isPending, loadOptions, loading, review, status]);

  const closeReview = React.useCallback(() => {
    if (saving) return;
    setOpen(false);
    setSearch('');
    setError('');
  }, [saving]);

  const toggleMedication = React.useCallback((medicationId) => {
    setSelectedIds((current) => (
      current.includes(medicationId)
        ? current.filter((id) => id !== medicationId)
        : [...current, medicationId]
    ));
  }, []);

  const selectScheduled = React.useCallback(() => {
    setSelectedIds(options.filter((option) => option.scheduledForDetectedTime).map((option) => option.id));
  }, [options]);

  const confirm = React.useCallback(async () => {
    if (!entryId || !selectedIds.length || saving || operationInFlightRef.current) return;
    const administrations = buildConfirmedMedicationAdministrations(
      options,
      selectedIds,
      administrationStatus
    );
    if (!administrations.length) return;

    operationInFlightRef.current = true;
    setSaving(true);
    setError('');
    try {
      const first = administrations[0];
      const isPlural = administrations.length > 1;
      const patch = {
        administrationConfirmed: true,
        medicationAdministrations: administrations,
        medicationId: isPlural ? null : first.medicationId,
        medicationName: isPlural ? null : first.medicationName,
        dose: isPlural ? null : first.dose,
        unit: isPlural ? null : first.unit,
        'metadata.medicationAdministrationReview.status': 'confirmed',
        'metadata.medicationAdministrationReview.confirmedAt': serverTimestamp(),
        'metadata.medicationAdministrationReview.confirmedBy': auth.currentUser?.uid || null,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, entryCollection, entryId), patch);
      setConfirmedAdministrations(administrations);
      setStatus('confirmed');
      setOpen(false);
      window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
        detail: { childId, entryId, collection: entryCollection, source: 'medication_administration_confirmed' },
      }));
    } catch (saveError) {
      setError(saveError?.message || 'Could not confirm this medication administration.');
    } finally {
      operationInFlightRef.current = false;
      setSaving(false);
    }
  }, [administrationStatus, childId, entryCollection, entryId, options, saving, selectedIds]);

  const dismiss = React.useCallback(async () => {
    if (!entryId || !isPending || saving || operationInFlightRef.current) return;
    if (!window.confirm(copy.dismissPrompt)) return;
    operationInFlightRef.current = true;
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, entryCollection, entryId), {
        'metadata.medicationAdministrationReview.status': 'dismissed',
        'metadata.medicationAdministrationReview.dismissedAt': serverTimestamp(),
        'metadata.medicationAdministrationReview.dismissedBy': auth.currentUser?.uid || null,
        updatedAt: serverTimestamp(),
      });
      setStatus('dismissed');
      window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
        detail: { childId, entryId, collection: entryCollection, source: 'medication_administration_dismissed' },
      }));
    } catch (dismissError) {
      setError(dismissError?.message || 'Could not leave this medication unconfirmed.');
    } finally {
      operationInFlightRef.current = false;
      setSaving(false);
    }
  }, [childId, copy.dismissPrompt, entryCollection, entryId, isPending, saving]);

  const openAddMedication = React.useCallback(() => {
    setAddDraft(createMedicationDetail({
      name: review?.medicationName || '',
      time: review?.localTime || '',
      schedules: [{ time: review?.localTime || '', dose: '', unit: 'mg' }],
    }));
    setAddOpen(true);
  }, [review?.localTime, review?.medicationName]);

  const updateAddDraft = React.useCallback((field, value) => {
    setAddDraft((current) => ({ ...current, [field]: value }));
  }, []);

  const saveAddedMedication = React.useCallback(async () => {
    if (!addDraft || !childId || saving || operationInFlightRef.current) return;
    operationInFlightRef.current = true;
    setSaving(true);
    setError('');
    try {
      const saved = await saveMedicationRecord({
        childId,
        medication: addDraft,
        status: 'saved',
        createTimelineEvent: true,
      });
      const nextOptions = await loadOptions();
      if (nextOptions.some((option) => option.id === saved.id)) {
        setSelectedIds((current) => [...new Set([...current, saved.id])]);
      }
      setAddOpen(false);
      setAddDraft(null);
    } catch (addError) {
      setError(addError?.message || 'Could not add this medication.');
    } finally {
      operationInFlightRef.current = false;
      setSaving(false);
    }
  }, [addDraft, childId, loadOptions, saving]);

  return {
    review,
    copy,
    status,
    isPending,
    confirmedAdministrations,
    open,
    options,
    selectedIds,
    administrationStatus,
    search,
    loading,
    saving,
    error,
    addOpen,
    addDraft,
    openReview,
    closeReview,
    toggleMedication,
    selectScheduled,
    setSearch,
    setAdministrationStatus,
    confirm,
    dismiss,
    openAddMedication,
    closeAddMedication: () => setAddOpen(false),
    updateAddDraft,
    saveAddedMedication,
  };
};

export default useMedicationAdministrationReview;
